'use client';
import { useState } from 'react';
import Link from 'next/link';
import { trackLead } from '@/lib/analytics';

export default function EbookPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !email.includes('@')) { setError('Skriv inn en gyldig e-postadresse'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/ebook-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      trackLead('ebook_download', { content_category: 'lead_magnet' });
    } catch {
      setError('Noe gikk galt. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  const questions = [
    { q: 'Hva er utdanningen og sertifiseringen din?', desc: 'Unngå selverklærte coacher uten faglig bakgrunn.' },
    { q: 'Kan du gi meg referanser?', desc: 'Ekte resultater fra tidligere klienter.' },
    { q: 'Hva er din spesialisering?', desc: 'En generalist er sjelden den beste for ditt mål.' },
    { q: 'Hva koster det, og hva er inkludert?', desc: 'Unngå skjulte kostnader og uklare avtaler.' },
    { q: 'Hvordan måler du fremgang?', desc: 'Konkrete mål, ikke magefølelse.' },
    { q: 'Hva skjer om det ikke fungerer?', desc: 'Trygghet med angrefrist og garanti.' },
    { q: 'Kan jeg få en gratis introduksjonssamtale?', desc: 'Kjemi og tillit er avgjørende.' },
  ];

  return (
    <div className="min-h-screen gradient-warm">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center">
          <Link href="/" className="flex items-center gap-2">
            <svg viewBox="0 0 32 32" fill="none" className="w-5 h-5">
              <circle cx="16" cy="16" r="11" stroke="#44403c" strokeWidth="1"/>
              <circle cx="16" cy="16" r="7.5" stroke="#57534e" strokeWidth="0.7"/>
              <polygon points="16,6 17.5,13 16,12 14.5,13" fill="#ffffff"/>
              <polygon points="16,26 14.5,19 16,20 17.5,19" fill="#78716c"/>
              <path d="M16,5 A11,11 0 1,0 16,27" stroke="#f5f5f4" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
            </svg>
            <span className="font-semibold text-stone-800 text-sm">CoachGuiden</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Gratis PDF-guide
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4 leading-tight">
            7 spørsmål du bør stille<br className="hidden sm:block" /> en coach før du betaler
          </h1>
          <p className="text-stone-500 text-lg max-w-xl mx-auto">
            Uansett om du leter etter en personlig trener, kostholdsveileder eller mentalcoach
            — still disse spørsmålene for å unngå feilkjøp.
          </p>
        </div>

        {/* Content grid */}
        <div className="grid md:grid-cols-5 gap-8 mb-12">
          {/* Left: questions list */}
          <div className="md:col-span-3 space-y-3">
            {questions.map((item, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-xl p-4 flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 bg-stone-900 text-white rounded-lg flex items-center justify-center text-sm font-semibold">
                  {i + 1}
                </div>
                <div>
                  <div className="font-medium text-stone-900 text-sm">{item.q}</div>
                  <div className="text-stone-500 text-xs mt-0.5">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: download form */}
          <div className="md:col-span-2">
            <div className="sticky top-20 bg-white border border-stone-200 rounded-2xl p-6">
              {done ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-1">Sjekk innboksen din!</h3>
                  <p className="text-stone-500 text-sm">Vi har sendt guiden til {email}.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-semibold text-stone-900 mb-1">Last ned gratis guide</h3>
                  <p className="text-stone-500 text-xs mb-4">
                    Du får guiden rett i innboksen — ingen spam, ingen forpliktelser.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="email"
                      placeholder="din@epost.no"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-stone-400"
                      required
                    />
                    {error && <p className="text-red-500 text-xs">{error}</p>}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <span>Sender...</span>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          Last ned PDF
                        </>
                      )}
                    </button>
                  </form>
                  <p className="text-stone-400 text-[10px] mt-3 leading-relaxed">
                    Ved å laste ned godtar du at vi sender deg guiden på e-post. Vi deler aldri e-postadressen din.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Trust section */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center mb-8">
          <h2 className="font-semibold text-stone-900 mb-2">Slipp å gjøre research selv</h2>
          <p className="text-stone-500 text-sm max-w-lg mx-auto mb-4">
            CoachGuiden verifiserer bakgrunn, sertifiseringer og referanser for alle coacher på plattformen — slik at du kan fokusere på å nå målene dine.
          </p>
          <Link
            href="/"
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-stone-800 transition-colors"
          >
            Finn din coach gratis
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </Link>
        </div>

        {/* Back to resources */}
        <div className="text-center">
          <Link href="/ressurser" className="text-stone-400 text-sm hover:text-stone-600 transition-colors">
            ← Tilbake til ressurser
          </Link>
        </div>
      </main>
    </div>
  );
}
